import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faSignOutAlt, 
  faCheckCircle,
  faExclamationCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

function Approval() {
  const navigate = useNavigate();

  const steps = [
    { id: 1, name: 'Account Created', status: 'completed' },
    { id: 2, name: 'Under Review', status: 'current' },
    { id: 3, name: 'Approved', status: 'upcoming' },
  ];

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '450px',
      padding: '40px',
      textAlign: 'center',
      margin: '20px'
    },
    logo: {
      marginBottom: '25px',
      textAlign: 'center'
    },
    logoH2: {
      color: '#2c3e50',
      fontSize: '1.8rem',
      marginBottom: '5px',
      marginTop: 0
    },
    logoP: {
      color: '#7f8c8d',
      fontSize: '0.85rem',
      margin: 0
    },
    header: {
      marginBottom: '30px'
    },
    iconCircle: {
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'center'
    },
    iconWrapper: {
      width: '70px',
      height: '70px',
      borderRadius: '50%',
      backgroundColor: '#e8f4fd',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    title: {
      color: '#2c3e50',
      fontSize: '1.5rem',
      marginBottom: '10px',
      fontWeight: '600',
      marginTop: 0
    },
    subtitle: {
      color: '#7f8c8d',
      fontSize: '0.85rem',
      margin: 0,
      lineHeight: '1.4'
    },
    progressContainer: {
      marginBottom: '30px',
      width: '100%'
    },
    stepsContainer: {
      display: 'flex',
      width: '100%',
      marginBottom: '12px',
      marginLeft: '40px',
    },
    stepWrapper: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      position: 'relative'
    },
    stepCircle: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.85rem',
      fontWeight: '600',
      zIndex: 2,
      backgroundColor: 'white',
      border: '2px solid',
      transition: 'all 0.3s ease',
      flexShrink: 0
    },
    stepLine: {
      flex: 1,
      height: '2px',
      transition: 'all 0.3s ease',
      margin: '0 8px'
    },
    stepLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '8px',
      width: '100%'
    },
    stepLabel: {
      fontSize: '0.7rem',
      textAlign: 'center',
      flex: 1,
      lineHeight: '1.3',
      padding: '0 4px'
    },
    statusCard: {
      backgroundColor: '#fff9e6',
      border: '1px solid #ffe5b4',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '25px',
      textAlign: 'left'
    },
    statusHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px'
    },
    statusTitle: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: '#856404',
      marginBottom: '10px',
      marginTop: 0
    },
    statusList: {
      margin: 0,
      paddingLeft: '20px',
      fontSize: '0.75rem',
      color: '#856404'
    },
    statusItem: {
      marginBottom: '6px',
      lineHeight: '1.4'
    },
    buttonOutline: {
      width: '100%',
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid #ddd',
      backgroundColor: 'white',
      color: '#7f8c8d',
      fontWeight: '500',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontFamily: 'inherit'
    },
    footer: {
      marginTop: '30px',
      fontSize: '0.75rem',
      color: '#95a5a6',
      borderTop: '1px solid #ecf0f1',
      paddingTop: '20px'
    }
  };

  // Add global styles for animations
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
      }
      
      .pulse-animation {
        animation: pulse 2s ease-in-out infinite;
      }
      
      button:hover:not(:disabled) {
        background-color: #f8f9fa !important;
        border-color: #bbb !important;
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const getStepStyle = (status) => {
    switch(status) {
      case 'completed':
        return {
          backgroundColor: '#27ae60',
          borderColor: '#27ae60',
          color: 'white'
        };
      case 'current':
        return {
          backgroundColor: '#3498db',
          borderColor: '#3498db',
          color: 'white'
        };
      default:
        return {
          backgroundColor: 'white',
          borderColor: '#ddd',
          color: '#95a5a6'
        };
    }
  };

  const getLineStyle = (status) => {
    return status === 'completed' 
      ? { backgroundColor: '#27ae60' } 
      : { backgroundColor: '#e0e0e0' };
  };

  return (
    <div style={{
      backgroundColor: '#f5f7fa',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      margin: 0,
      padding: '20px'
    }}>
      <div style={styles.container}>
        <div style={styles.logo}>
          <h2 style={styles.logoH2}>CraneMonitor Pro</h2>
          <p style={styles.logoP}>Industrial Crane Monitoring System</p>
        </div>

        <div style={styles.header}>
          <div style={styles.iconCircle}>
            <div style={styles.iconWrapper}>
              <FontAwesomeIcon icon={faClock} size="2x" color="#3498db" className="pulse-animation" />
            </div>
          </div>
          <h2 style={styles.title}>Awaiting Approval</h2>
          <p style={styles.subtitle}>Your account is being reviewed by the organization administrator</p>
        </div>

        {/* Progress Steps */}
        <div style={styles.progressContainer}>
          <div style={styles.stepsContainer}>
            {steps.map((step, index) => (
              <div key={step.id} style={styles.stepWrapper}>
                <div style={{
                  ...styles.stepCircle,
                  ...getStepStyle(step.status)
                }}>
                  {step.status === 'completed' ? (
                    <FontAwesomeIcon icon={faCheckCircle} size="sm" />
                  ) : step.status === 'current' ? (
                    <FontAwesomeIcon icon={faSpinner} size="sm" className="pulse-animation" />
                  ) : (
                    step.id
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div style={{
                    ...styles.stepLine,
                    ...getLineStyle(step.status)
                  }} />
                )}
              </div>
            ))}
          </div>
          <div style={styles.stepLabels}>
            {steps.map((step) => (
              <div key={step.id} style={{
                ...styles.stepLabel,
                color: step.status === 'current' ? '#3498db' : (step.status === 'completed' ? '#27ae60' : '#95a5a6'),
                fontWeight: step.status === 'current' ? '600' : 'normal'
              }}>
                {step.name}
              </div>
            ))}
          </div>
        </div>

        {/* Status Card */}
        <div style={styles.statusCard}>
          <div style={styles.statusHeader}>
            <FontAwesomeIcon icon={faExclamationCircle} size="lg" color="#856404" style={{ marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <h3 style={styles.statusTitle}>What happens next?</h3>
              <ul style={styles.statusList}>
                <li style={styles.statusItem}>Administrator will review your account details</li>
                <li style={styles.statusItem}>You'll receive an email notification upon approval</li>
                <li style={styles.statusItem}>Typical approval time: 24-48 hours</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ marginTop: '20px' }}>
          <button
            onClick={() => navigate('/login')}
            style={styles.buttonOutline}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
              e.target.style.borderColor = '#bbb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.borderColor = '#ddd';
            }}
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
            Sign Out
          </button>
        </div>

        <div style={styles.footer}>
          <p style={{ margin: 0 }}>© 2026 CraneMonitor Pro. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default Approval;